import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import moment from 'moment';
import { Info, Paperclip, PencilSimple, Plus } from 'phosphor-react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import cx from 'classnames';
import { Menu } from '@headlessui/react';
import compact from 'lodash/compact';

import {
  createEmployeeReport,
  createContractorReport,
  reportName,
  reportValidatedFileName,
  getReportResultCSV,
  getValidatedFileBlob,
  reportResultFileName
} from 'api/reports';
import { DOWNLOAD_REPORT_STEP, REVIEW_DATA_STEP } from 'constants/report-steps';
import { Report } from 'graphql-api';
import Spinner from 'components/spinner';
import { reportCurrentStepPath } from 'utils/path';
import { navigateAndStoreStep } from 'utils/navigation';
import { downloadBlob, downloadCSV } from 'utils/file';
import { CONTRACTOR_REPORT_TYPE, EMPLOYEE_REPORT_TYPE } from 'constants/common';
import IntroduceOpencompCard from 'components/dashboard-page/introduce-opencomp-card';

function DashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const {
    employeeReport,
    contractorReport
  }: { employeeReport: Report | null; contractorReport: Report | null } = useOutletContext();
  const [isCreatingEmployeeReport, setIsCreatingEmployeeReport] = useState(false);
  const [isCreatingContractorReport, setIsCreatingContractorReport] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState<Record<string, boolean>>({
    [CONTRACTOR_REPORT_TYPE]: false,
    [EMPLOYEE_REPORT_TYPE]: false
  });
  const [isDownloadingSourceFile, setIsDownloadingSourceFile] = useState<Record<string, boolean>>({
    [CONTRACTOR_REPORT_TYPE]: false,
    [EMPLOYEE_REPORT_TYPE]: false
  });
  const reports = compact([employeeReport, contractorReport]);
  const enableCreateEmployeeReport = employeeReport === null || employeeReport === undefined;
  const enableCreateContractorReport = contractorReport === null || contractorReport === undefined;
  const enableCreateReport = enableCreateEmployeeReport || enableCreateContractorReport;

  const handleNewEmployeeReport = (): void => {
    void (async () => {
      if (enableCreateEmployeeReport) {
        setIsCreatingEmployeeReport(true);
        const createdEmployeeReport = await createEmployeeReport();
        if (createdEmployeeReport !== null && createdEmployeeReport !== undefined) {
          const navigatePath = reportCurrentStepPath(createdEmployeeReport);
          if (navigatePath !== null) {
            setIsCreatingEmployeeReport(false);
            navigate(navigatePath);
          }
        }
      }
    })();
  };

  const handleNewContractorReport = (): void => {
    void (async () => {
      if (enableCreateContractorReport) {
        setIsCreatingContractorReport(true);
        const createdContractorReport = await createContractorReport();
        if (createdContractorReport !== null && createdContractorReport !== undefined) {
          const navigatePath = reportCurrentStepPath(createdContractorReport);
          if (navigatePath !== null) {
            setIsCreatingContractorReport(false);
            navigate(navigatePath);
          }
        }
      }
    })();
  };

  const handleEditClick = (report: Report): void => {
    if (report.paid === true && report.currentStep === DOWNLOAD_REPORT_STEP) {
      void navigateAndStoreStep(report, REVIEW_DATA_STEP, navigate);
    } else {
      navigate(reportCurrentStepPath(report));
    }
  };

  const handleDownloadReport = (report: Report): void => {
    void (async () => {
      const reportType = report.reportType;
      setIsDownloadingReport((state) => ({ ...state, [reportType]: true }));
      const reportFile = await getReportResultCSV(report?.id);
      if (reportFile !== null) {
        downloadCSV(await reportFile.text(), reportResultFileName(report));
      }
      setIsDownloadingReport((state) => ({ ...state, [reportType]: false }));
    })();
  };

  const handleDownloadValidatedFile = (report: Report): void => {
    void (async () => {
      const reportType = report.reportType;
      setIsDownloadingSourceFile((state) => ({ ...state, [reportType]: true }));
      const blob = await getValidatedFileBlob(report);
      blob !== null && downloadBlob(blob, reportValidatedFileName(report));
      setIsDownloadingSourceFile((state) => ({ ...state, [reportType]: false }));
    })();
  };

  return (
    <>
      <div className="border-b border-gray-200 px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-medium leading-6 text-gray-900 sm:truncate">Home</h1>
        </div>
        <div className="mt-4 flex sm:mt-0 sm:ml-4">
          <Menu as="div" className="relative inline-block text-left">
            {({ open }) => (
              <>
                <Menu.Button className="btn-primary" disabled={!enableCreateReport}>
                  <div
                    data-tooltip-place="bottom"
                    data-tooltip-offset={15}
                    data-tooltip-id="new-report-tooltip"
                    className="inline-flex items-center justify-center w-full h-full">
                    <Plus size={14} weight="bold" className="mr-2" />
                    New report
                  </div>
                </Menu.Button>
                <Tooltip
                  id="new-report-tooltip"
                  content={
                    enableCreateReport
                      ? undefined
                      : 'Companies are only required to submit up to two reports — one employee report and one labor contractor report. If you need to make changes, please edit an existing report.'
                  }
                  className="bg-gray-900 text-white w-56 rounded px-2 py-1 text-sm font-normal text-center opacity-100"
                />

                {(open || isCreatingEmployeeReport || isCreatingContractorReport) && (
                  <div>
                    <Menu.Items
                      static
                      className="absolute right-0 z-10 mt-1 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {enableCreateEmployeeReport && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={cx(
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                'block w-full px-3 py-2 text-sm cursor-pointer w-fit whitespace-nowrap'
                              )}
                              disabled={isCreatingEmployeeReport}
                              onClick={handleNewEmployeeReport}>
                              New Payroll Employees Report&nbsp;
                              {isCreatingEmployeeReport && <Spinner className="ml-2" />}
                            </button>
                          )}
                        </Menu.Item>
                      )}
                      {enableCreateContractorReport && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={cx(
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                'block w-full px-3 py-2 text-sm cursor-pointer w-fit whitespace-nowrap'
                              )}
                              disabled={isCreatingContractorReport}
                              onClick={handleNewContractorReport}>
                              New Labor Contractor Report&nbsp;
                              {isCreatingContractorReport && <Spinner className="ml-2" />}
                            </button>
                          )}
                        </Menu.Item>
                      )}
                    </Menu.Items>
                  </div>
                )}
              </>
            )}
          </Menu>
        </div>
      </div>
      {reports.length === 0 ? (
        <div className="mt-32 text-center italic">
          There are no reports yet. Click &quot;New report&quot; above to create a new report.
        </div>
      ) : (
        <>
          <div className="mt-8 sm:mx-6 lg:mx-8 pb-8 border-b border-gray-200">
            <div className="grid grid-cols-2 gap-6">
              {reports.map((report) => (
                <div className="border-gray-200" key={report.id}>
                  <div
                    className={cx('border rounded-lg', {
                      'rounded-b-none':
                        report.validatedFile !== null && report.validatedFile !== undefined
                    })}>
                    <div className="px-6 py-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
                      <div className="grow">
                        <h3 className="text-xl font-normal leading-8 text-gray-900">
                          {reportName(report.reportType, report.year)}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Created {moment(report.createdAt).format('MM/DD/YYYY')}
                        </p>
                      </div>
                      <div className="ml-6 flex self-center">
                        <div
                          data-testid="edit-btn"
                          data-tooltip-id="edit-report-tooltip"
                          data-tooltip-place="bottom"
                          data-tooltip-offset={6}
                          onClick={() => handleEditClick(report)}
                          className="text-gray-600 mr-2 p-3 rounded cursor-pointer flex hover:bg-gray-100">
                          <PencilSimple size={17} weight="bold" className="self-center" />
                        </div>
                        {report?.paid === true && report?.resultGenerated === true && (
                          <button
                            data-tooltip-id="download-report-tooltip"
                            data-tooltip-place="bottom"
                            data-tooltip-offset={6}
                            type="button"
                            className="btn-secondary"
                            onClick={() => handleDownloadReport(report)}>
                            Download&nbsp;
                            {isDownloadingReport[report.reportType] && <Spinner className="ml-2" />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {report.validatedFile !== null && report.validatedFile !== undefined && (
                    <div className="px-6 py-6 text-sm border border-t-0 rounded-b-lg">
                      <div className="text-gray-500 font-semibold flex">
                        Additional files (non-fillable)
                        <Info
                          data-tooltip-id="info-tooltip"
                          data-tooltip-place="bottom"
                          data-tooltip-offset={6}
                          size={15}
                          className="text-gray-500 ml-1 cursor-pointer self-center"
                          weight="bold"
                        />
                      </div>
                      <div className="mt-1 border rounded py-3 flex px-4">
                        <Paperclip
                          size={18}
                          className="text-gray-400 inline mr-3 cursor-pointer self-center"
                          weight="bold"
                        />
                        <div className="grow truncate">{reportValidatedFileName(report)}</div>
                        <a
                          data-tooltip-id="download-employee-file-tooltip"
                          data-tooltip-place="bottom"
                          data-tooltip-offset={6}
                          className="text-blue-500 font-semibold cursor-pointer"
                          onClick={() => handleDownloadValidatedFile(report)}>
                          Download&nbsp;
                          {isDownloadingSourceFile[report.reportType] && (
                            <Spinner className="ml-2" />
                          )}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <Tooltip
                id="edit-report-tooltip"
                content="Edit report"
                className="bg-gray-900 text-white w-fit rounded px-2 py-1 text-sm"
              />
              <Tooltip
                id="download-report-tooltip"
                content="Download report"
                className="bg-gray-900 text-white w-fit rounded px-2 py-1 text-sm"
              />
              <Tooltip
                id="download-employee-file-tooltip"
                content="Download file"
                className="bg-gray-900 text-white w-fit rounded px-2 py-1 text-sm"
              />
              <Tooltip
                id="info-tooltip"
                content="The file below is not intended for upload to the CA Pay Data Reporting Portal. This file contains only your employee-level data, which can be used to update your HRIS with the info compiled for your report."
                className="bg-gray-900 text-white w-56 rounded px-2 py-1 text-sm text-center opacity-100"
              />
            </div>
          </div>
          <div className="py-8 mx-8">
            <IntroduceOpencompCard />
          </div>
        </>
      )}
    </>
  );
}

export default DashboardPage;
